import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSun, faMoon, faEye, faEyeSlash, faLock, faCheck, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import logo from '../assets/logo.png';

const DARK = {
  mode: "dark",
  accent: "#0C69C8",
  accentBgSubtle: "rgba(12,105,200,0.08)",
  accentBgLight: "rgba(12,105,200,0.12)",
  accentBorder: "rgba(12,105,200,0.20)",
  accentGrad: "linear-gradient(135deg, #0C69C8 0%, #0A1D44 100%)",
  accentGlow: "rgba(12,105,200,0.35)",
  bg: "#0A0A0A",
  bgCard: "#141414",
  bgAlt: "#111111",
  bgElevated: "#1E1E1E",
  border: "rgba(255,255,255,0.08)",
  borderMedium: "rgba(255,255,255,0.12)",
  shadowMd: "0 4px 16px rgba(0,0,0,0.4)",
  shadowLg: "0 12px 40px rgba(0,0,0,0.5)",
  text: "#FAFAFA",
  textSecondary: "rgba(255,255,255,0.70)",
  textMuted: "rgba(255,255,255,0.40)",
  headerBg: "rgba(10,10,10,0.92)",
  scrollbarThumb: "rgba(255,255,255,0.12)",
  inputBg: "rgba(255,255,255,0.04)",
  inputBorder: "rgba(255,255,255,0.10)",
  inputText: "#FFFFFF",
  inputPlaceholder: "rgba(255,255,255,0.35)",
  danger: "#EF4444",
  success: "#34D399",
};

const LIGHT = {
  mode: "light",
  accent: "#0C69C8",
  accentBgSubtle: "rgba(12,105,200,0.05)",
  accentBgLight: "rgba(12,105,200,0.08)",
  accentBorder: "rgba(12,105,200,0.15)",
  accentGrad: "linear-gradient(135deg, #0C69C8 0%, #0A1D44 100%)",
  accentGlow: "rgba(12,105,200,0.25)",
  bg: "#F5F7FA",
  bgCard: "#FFFFFF",
  bgAlt: "#EDF0F5",
  bgElevated: "#FFFFFF",
  border: "rgba(10,29,68,0.08)",
  borderMedium: "rgba(10,29,68,0.12)",
  shadowMd: "0 4px 16px rgba(10,29,68,0.08)",
  shadowLg: "0 12px 40px rgba(10,29,68,0.12)",
  text: "#0F1629",
  textSecondary: "#3D4A6B",
  textMuted: "#7A87A8",
  headerBg: "rgba(245,247,250,0.95)",
  scrollbarThumb: "rgba(10,29,68,0.12)",
  inputBg: "#F8F9FC",
  inputBorder: "rgba(10,29,68,0.12)",
  inputText: "#0F1629",
  inputPlaceholder: "rgba(10,29,68,0.35)",
  danger: "#DC2626",
  success: "#059669",
};

const ResetPassword = () => {
  const [isDark, setIsDark] = useState(() => {
    try { return localStorage.getItem("spa-theme") !== "light"; } catch { return true; }
  });
  const C = isDark ? DARK : LIGHT;

  const toggleTheme = useCallback(() => {
    setIsDark((v) => {
      const next = !v;
      try { localStorage.setItem("spa-theme", next ? "dark" : "light"); } catch {}
      return next;
    });
  }, []);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sessionReady, setSessionReady] = useState(false);
  const [status, setStatus] = useState(null);
  const [statusMsg, setStatusMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const restoreSession = async () => {
      setLoading(true);
      setStatus(null);
      setStatusMsg('');

      const params = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      const code = params.get('code');
      const tokenHash = params.get('token_hash');
      const type = params.get('type');
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');

      const markReady = () => {
        setSessionReady(true);
        if (window.location.search || window.location.hash) {
          window.history.replaceState(null, '', '/reset-password');
        }
      };

      try {
        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) throw error;
          markReady();
          setLoading(false);
          return;
        }

        if (accessToken && refreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (error) throw error;
          markReady();
          setLoading(false);
          return;
        }

        if (tokenHash && type) {
          const { error } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: 'recovery',
          });
          if (error) throw error;
          markReady();
          setLoading(false);
          return;
        }

        // Fallback: check if there's already an active recovery session.
        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;
        if (data?.session) {
          setSessionReady(true);
        }
      } catch (error) {
        // With detectSessionInUrl enabled, Supabase may exchange the PKCE code
        // itself just before this component tries to exchange it. In that race
        // the second exchange fails even though the recovery session is valid.
        const { data: currentSession } = await supabase.auth.getSession();
        if (currentSession?.session) {
          markReady();
          setLoading(false);
          return;
        }
        setSessionReady(false);
        setStatus('error');
        setStatusMsg(error?.message || 'This reset link could not be verified. Please request a new password reset email.');
      }

      setLoading(false);
    };

    restoreSession();
  }, []);

  const requirements = [
    { label: "At least 8 characters", met: newPassword.length >= 8 },
    { label: "One uppercase letter", met: /[A-Z]/.test(newPassword) },
    { label: "One lowercase letter", met: /[a-z]/.test(newPassword) },
    { label: "One number", met: /\d/.test(newPassword) },
    { label: "One special character", met: /[\W_]/.test(newPassword) },
  ];

  const allMet = requirements.every((r) => r.met);
  const passwordsMatch = newPassword.length > 0 && confirmPassword.length > 0 && newPassword === confirmPassword;

  const handleReset = async () => {
    if (!sessionReady) {
      setStatus('error');
      setStatusMsg('This reset link is not active. Please request a new password reset email.');
      return;
    }

    if (!newPassword || !allMet) {
      setStatus('error');
      setStatusMsg('Please choose a password that meets every requirement.');
      return;
    }

    if (!passwordsMatch) {
      setStatus('error');
      setStatusMsg('Both password fields must match before you can update your password.');
      return;
    }

    setSubmitting(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    setSubmitting(false);
    if (error) {
      setStatus('error');
      setStatusMsg(error?.message || 'Failed to reset password. Please request a new reset link and try again.');
    } else {
      setStatus('success');
      setStatusMsg('Password updated successfully!');
      setTimeout(() => navigate('/'), 2000);
    }
  };

  const inputStyle = () => ({
    width: "100%",
    padding: "13px 42px 13px 44px",
    borderRadius: 12,
    border: `1px solid ${C.inputBorder}`,
    background: C.inputBg,
    color: C.inputText,
    fontSize: 14,
    fontFamily: "'DM Sans', sans-serif",
    outline: "none",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
  });

  if (loading) {
    return (
      <div style={{
        background: C.bg, minHeight: "100vh", display: "flex",
        alignItems: "center", justifyContent: "center",
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: "50%",
          border: `3px solid ${C.accentBorder}`,
          borderTopColor: C.accent,
          animation: "spin 0.8s linear infinite",
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{
      background: C.bg, minHeight: "100vh", color: C.text,
      fontFamily: "'DM Sans', sans-serif",
      transition: "background 0.3s ease, color 0.3s ease",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@700;800&family=DM+Sans:wght@300;400;500;600;700&display=swap');
        * { margin: 0; padding: 0; box-sizing: border-box; -webkit-font-smoothing: antialiased; }
        a { color: inherit; text-decoration: none; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${C.scrollbarThumb}; border-radius: 99px; }
        .theme-toggle { transition: transform 0.2s ease; }
        .theme-toggle:hover { transform: rotate(15deg) scale(1.1); }
        .grad-text {
          background: linear-gradient(135deg, #0C69C8 0%, #0A1D44 100%);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
        }
        .reset-input:focus {
          border-color: rgba(12,105,200,0.5) !important;
          box-shadow: 0 0 0 3px rgba(12,105,200,0.10) !important;
        }
        .btn-primary { transition: transform 0.2s ease, box-shadow 0.3s ease; }
        .btn-primary:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 8px 28px ${C.accentGlow};
        }
        .btn-primary:active:not(:disabled) { transform: translateY(1px); }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .fade-in { animation: fadeIn 0.4s ease forwards; }
      `}</style>

      {/* Header */}
      <header style={{
        position: "sticky", top: 0, zIndex: 100,
        height: 64, padding: "0 24px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: C.headerBg,
        backdropFilter: "blur(20px) saturate(1.2)",
        WebkitBackdropFilter: "blur(20px) saturate(1.2)",
        borderBottom: `1px solid ${C.border}`,
        transition: "background 0.3s ease",
      }}>
        <Link to="/" style={{ display: "flex", alignItems: "center" }}>
          <img src={logo} alt="Snows ProAm" style={{ height: 30 }} />
        </Link>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Link to="/" style={{
            fontSize: 13.5, fontWeight: 500, color: C.textSecondary,
            padding: "6px 14px", borderRadius: 8,
            border: `1px solid ${C.border}`, background: C.bgElevated,
          }}>← Back</Link>
          <button className="theme-toggle" aria-label={isDark ? "Use light theme" : "Use dark theme"} onClick={toggleTheme}
            style={{
              width: 36, height: 36, borderRadius: 10,
              background: C.bgElevated, border: `1px solid ${C.border}`,
              color: C.text, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
            }}>
            <FontAwesomeIcon icon={isDark ? faSun : faMoon} style={{ fontSize: 13 }} />
          </button>
        </div>
      </header>

      {/* Main */}
      <div style={{
        minHeight: "calc(100vh - 64px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "40px 24px",
      }}>
        <div className="fade-in" style={{ width: "min(460px, 100%)" }}>

          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "6px 14px", borderRadius: 999,
            background: C.accentBgSubtle, border: `1px solid ${C.accentBorder}`,
            fontSize: 12, fontWeight: 600, color: C.accent,
            textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 20,
          }}>Account Security</div>

          <h1 style={{
            fontFamily: "'Sora', sans-serif",
            fontSize: "clamp(28px, 5vw, 40px)",
            fontWeight: 800, letterSpacing: -1, lineHeight: 1.08,
            marginBottom: 10, color: C.text,
          }}>
            Reset your <span className="grad-text">Password</span>
          </h1>
          <p style={{ fontSize: 15, color: C.textSecondary, marginBottom: 32, lineHeight: 1.55 }}>
            Choose a strong password to keep your account secure.
          </p>

          {/* Card */}
          <div style={{
            background: C.bgCard,
            border: `1px solid ${C.border}`,
            borderRadius: 20,
            boxShadow: C.shadowLg,
            padding: 28,
            position: "relative",
            overflow: "hidden",
          }}>
          

            {!sessionReady ? (
              <div style={{ textAlign: "center", padding: "12px 0" }}>
                <div style={{
                  width: 52, height: 52, borderRadius: 14,
                  background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.18)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  margin: "0 auto 16px",
                }}>
                  <FontAwesomeIcon icon={faLock} style={{ fontSize: 20, color: C.danger }} />
                </div>
                <h3 style={{ fontFamily: "'Sora', sans-serif", fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 8 }}>
                  Link expired or invalid
                </h3>
                <p style={{ fontSize: 14, color: C.textSecondary, lineHeight: 1.6, marginBottom: 20 }}>
                  {statusMsg || 'This password reset link is no longer valid. Please request a new one from the sign-in page.'}
                </p>
                <Link to="/" style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  background: C.accentGrad, color: "#fff",
                  padding: "11px 22px", borderRadius: 10,
                  fontSize: 14, fontWeight: 600, fontFamily: "'DM Sans', sans-serif",
                }}>
                  Back to Sign In <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 12 }} />
                </Link>
              </div>
            ) : status === 'success' ? (
              <div style={{ textAlign: "center", padding: "12px 0" }}>
                <div style={{
                  width: 52, height: 52, borderRadius: 14,
                  background: isDark ? "rgba(52,211,153,0.10)" : "rgba(5,150,105,0.08)",
                  border: `1px solid ${isDark ? "rgba(52,211,153,0.20)" : "rgba(5,150,105,0.15)"}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  margin: "0 auto 16px",
                }}>
                  <FontAwesomeIcon icon={faCheck} style={{ fontSize: 20, color: C.success }} />
                </div>
                <h3 style={{ fontFamily: "'Sora', sans-serif", fontSize: 17, fontWeight: 700, color: C.text, marginBottom: 8 }}>
                  Password updated!
                </h3>
                <p style={{ fontSize: 14, color: C.textSecondary, lineHeight: 1.6 }}>
                  {statusMsg} Redirecting you now…
                </p>
              </div>
            ) : (
              <form onSubmit={(e) => { e.preventDefault(); handleReset(); }} style={{ display: "grid", gap: 16 }}>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 600, color: C.textMuted, display: "block", marginBottom: 8 }}>
                    New Password
                  </label>
                  <div style={{ position: "relative" }}>
                    <div style={{
                      position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)",
                      color: C.textMuted, fontSize: 14, pointerEvents: "none",
                    }}>
                      <FontAwesomeIcon icon={faLock} />
                    </div>
                    <input
                      className="reset-input"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      autoComplete="new-password"
                      aria-describedby="password-requirements reset-status"
                      style={inputStyle()}
                    />
                    <button
                      type="button"
                      aria-label={showPassword ? "Hide new password" : "Show new password"}
                      onClick={() => setShowPassword((v) => !v)}
                      style={{
                        position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)",
                        background: "none", border: "none", cursor: "pointer",
                        color: C.textMuted, fontSize: 14, padding: 0,
                      }}>
                      <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                    </button>
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: 13, fontWeight: 600, color: C.textMuted, display: "block", marginBottom: 8 }}>
                    Confirm Password
                  </label>
                  <div style={{ position: "relative" }}>
                    <div style={{
                      position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)",
                      color: C.textMuted, fontSize: 14, pointerEvents: "none",
                    }}>
                      <FontAwesomeIcon icon={faLock} />
                    </div>
                    <input
                      className="reset-input"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Re-enter new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                      aria-describedby="password-match reset-status"
                      style={inputStyle()}
                    />
                    <button
                      type="button"
                      aria-label={showConfirmPassword ? "Hide confirmed password" : "Show confirmed password"}
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      style={{
                        position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)",
                        background: "none", border: "none", cursor: "pointer",
                        color: C.textMuted, fontSize: 14, padding: 0,
                      }}>
                      <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                    </button>
                  </div>
                  {confirmPassword.length > 0 && newPassword !== confirmPassword && (
                    <div id="password-match" style={{ marginTop: 7, color: C.danger, fontSize: 12, fontWeight: 600 }}>
                      Passwords do not match.
                    </div>
                  )}
                </div>

                {newPassword.length > 0 && (
                  <div id="password-requirements" style={{
                    padding: "14px 16px", borderRadius: 12,
                    background: C.bgAlt, border: `1px solid ${C.border}`,
                    display: "grid", gap: 7,
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
                      Requirements
                    </div>
                    {requirements.map((r) => (
                      <div key={r.label} style={{ display: "flex", alignItems: "center", gap: 9 }}>
                        <div style={{
                          width: 16, height: 16, borderRadius: "50%", flexShrink: 0,
                          background: r.met
                            ? (isDark ? "rgba(52,211,153,0.15)" : "rgba(5,150,105,0.10)")
                            : C.bgElevated,
                          border: `1px solid ${r.met ? C.success : C.border}`,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          transition: "all 0.2s ease",
                        }}>
                          {r.met && <FontAwesomeIcon icon={faCheck} style={{ fontSize: 8, color: C.success }} />}
                        </div>
                        <span style={{
                          fontSize: 13, color: r.met ? C.textSecondary : C.textMuted,
                          transition: "color 0.2s ease",
                        }}>{r.label}</span>
                      </div>
                    ))}
                  </div>
                )}

                {status === 'error' && (
                  <div id="reset-status" role="alert" style={{
                    padding: "10px 14px", borderRadius: 10,
                    background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.18)",
                    color: C.danger, fontSize: 13, fontWeight: 600,
                  }}>{statusMsg}</div>
                )}

                <button
                  type="submit"
                  className="btn-primary"
                  disabled={!allMet || !passwordsMatch || submitting}
                  style={{
                    width: "100%",
                    background: C.accentGrad, border: "none", color: "#fff",
                    padding: "14px", borderRadius: 12, cursor: (!allMet || !passwordsMatch || submitting) ? "not-allowed" : "pointer",
                    fontSize: 15, fontWeight: 700, fontFamily: "'Sora', sans-serif",
                    opacity: (!allMet || !passwordsMatch || submitting) ? 0.5 : 1,
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                    transition: "opacity 0.2s ease",
                  }}>
                  {submitting ? (
                    <>
                      <div style={{
                        width: 16, height: 16, borderRadius: "50%",
                        border: "2px solid rgba(255,255,255,0.3)",
                        borderTopColor: "#fff",
                        animation: "spin 0.8s linear infinite",
                      }} />
                      Updating…
                    </>
                  ) : (
                    <>
                      Update Password
                      <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 13 }} />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

          <p style={{ marginTop: 20, fontSize: 13, color: C.textMuted, textAlign: "center", lineHeight: 1.6 }}>
            Need help?{" "}
            <a href="mailto:support@snowsproam.com" style={{ color: C.accent, fontWeight: 600 }}>
              support@snowsproam.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
